/**
 * Centralized logging utility
 *
 * - Console output in all environments. warn/error console sinks print a static
 *   message only (no Error object, stack, or cause — CWE-209 / WS-JAVASCRIPT-01076).
 * - In production (or when enabled), full detail is shipped to backend for ELK/Kibana
 *   via POST /api/v1/telemetry/frontend-logs (service.name=tagp-frontend).
 * - Optional auth: pre-login logs are accepted by the backend.
 */

import { config } from '../config'
import { createServiceFetch } from '../utils/validateFetchUrl'

const logShipFetch = createServiceFetch(config.api.baseUrl)

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogContext {
  [key: string]: unknown
}

/** Single log entry for backend ingestion (ECS-style) */
interface FrontendLogEntry {
  level: string
  message: string
  timestamp: string
  url?: string
  context?: Record<string, unknown>
}

const FRONTEND_LOGS_ENDPOINT = '/v1/telemetry/frontend-logs'
const MAX_QUEUE = 50
const FLUSH_DEBOUNCE_MS = 10_000
const BATCH_FLUSH_THRESHOLD = 25
/** Max URL length sent to backend (backend also enforces; avoid token-in-URL validation errors) */
const MAX_LOG_URL_LENGTH = 2000
/** Query params to strip from URLs before logging (avoid logging tokens) */
const SENSITIVE_QUERY_PARAMS = ['access_token', 'id_token', 'refresh_token', 'code']

/**
 * Sanitize URL for telemetry: remove sensitive query params and cap length.
 * Prevents RequestValidationError when location.href contains long tokens and avoids logging tokens.
 */
function sanitizeUrlForLogging(url: string): string {
  if (!url) return url
  try {
    const u = new URL(url)
    SENSITIVE_QUERY_PARAMS.forEach((param) => u.searchParams.delete(param))
    const sanitized = u.toString()
    return sanitized.length > MAX_LOG_URL_LENGTH ? sanitized.slice(0, MAX_LOG_URL_LENGTH) : sanitized
  } catch {
    return url.length > MAX_LOG_URL_LENGTH ? url.slice(0, MAX_LOG_URL_LENGTH) : url
  }
}

/**
 * Backend FrontendLogEntry.context must be a plain object (not an array).
 * Arrays and primitives are wrapped so accidental logger.info(msg, list) calls
 * do not fail POST /telemetry/frontend-logs validation.
 */
function normalizeLogContext(context?: unknown): LogContext | undefined {
  if (context == null) return undefined
  if (Array.isArray(context)) {
    return { count: context.length, items: context.slice(0, 5) }
  }
  if (typeof context === 'object') {
    return context as LogContext
  }
  return { value: context }
}

/**
 * Safe JSON stringify that handles circular references and DOM elements
 */
function safeStringify(obj: unknown): string {
  const seen = new WeakSet()
  return JSON.stringify(obj, (key, value) => {
    // Skip DOM elements and React fiber nodes
    if (value instanceof HTMLElement || value instanceof Node) {
      return `[${value.constructor.name}]`
    }
    // Skip React synthetic events
    if (value && typeof value === 'object' && 'nativeEvent' in value) {
      return '[SyntheticEvent]'
    }
    // Skip functions
    if (typeof value === 'function') {
      return '[Function]'
    }
    // Handle circular references
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]'
      }
      seen.add(value)
    }
    return value
  })
}

/**
 * Recover a readable detail when Error(message) was built from a non-string
 * FastAPI `detail` object (coerces to "[object Object]"). Prefer ApiError.data.
 */
function resolveErrorDetail(err: Error | unknown): string {
  if (err == null) return ''
  if (err instanceof Error) {
    const raw = (err.message || '').trim()
    if (raw && raw !== '[object Object]') return raw
    const data = (err as { data?: unknown }).data
    if (data && typeof data === 'object') {
      try {
        return safeStringify(data).slice(0, 2000)
      } catch {
        return raw
      }
    }
    return raw
  }
  if (typeof err === 'string') return err
  if (typeof err === 'object') {
    try {
      return safeStringify(err).slice(0, 2000)
    } catch {
      return ''
    }
  }
  return String(err)
}

function joinLogMessage(message: string, detail: string): string {
  if (!detail) return message
  const base = message.replace(/:\s*$/, '')
  return `${base}: ${detail}`
}

function formatErrorLogParts(
  message: string,
  err: Error | unknown,
  ctx?: LogContext
): { logMessage: string; fullContext: LogContext } {
  const errorMessage = resolveErrorDetail(err)
  const fullContext: LogContext = {
    ...ctx,
    stack: err instanceof Error ? err.stack : undefined,
  }
  // Keep structured API payload in context for Kibana even when message is recovered
  if (err instanceof Error && (err as { data?: unknown }).data != null && fullContext.data == null) {
    fullContext.data = (err as { data?: unknown }).data
  }
  if (err instanceof Error && typeof (err as { status?: unknown }).status === 'number') {
    fullContext.httpStatus = (err as { status: number }).status
  }
  return {
    logMessage: joinLogMessage(message, errorMessage),
    fullContext,
  }
}

class Logger {
  private readonly logQueue: FrontendLogEntry[] = []
  private flushScheduled: ReturnType<typeof setTimeout> | null = null

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString()
    const contextStr = context ? ` ${safeStringify(context)}` : ''
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`
  }

  /** Get auth token for log shipping (optional; pre-login logs still accepted by backend) */
  private getAuthToken(): string | null {
    try {
      return localStorage.getItem(config.auth.tokenKey)
    } catch {
      return null
    }
  }

  /** Ship queued logs to backend (fire-and-forget). Canonical path for Kibana: service.name=tagp-frontend */
  private flushLogQueue(): void {
    if (this.logQueue.length === 0) return
    const batch = this.logQueue.splice(0, MAX_QUEUE)
    const baseUrl = (config.api.baseUrl || '').replace(/\/+$/, '')
    const url = `${baseUrl}${FRONTEND_LOGS_ENDPOINT}`
    const token = this.getAuthToken()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`
    logShipFetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ logs: batch }),
      keepalive: true,
    }).catch(() => { /* fire-and-forget */ })
  }

  /** Enqueue a log for shipping and schedule flush (non-blocking) */
  private enqueueForShipping(level: LogLevel, message: string, context?: LogContext): void {
    if (config.env.isDevelopment && !config.features.debug) return
    const entry: FrontendLogEntry = {
      level: level === 'warn' ? 'warn' : level,
      message: message.slice(0, 8000),
      timestamp: new Date().toISOString(),
      url:
        typeof window !== 'undefined'
          ? sanitizeUrlForLogging(window.location.href)
          : undefined,
      context: normalizeLogContext(context),
    }
    this.logQueue.push(entry)
    if (this.logQueue.length >= BATCH_FLUSH_THRESHOLD) {
      this.flushLogQueue()
      if (this.flushScheduled) {
        clearTimeout(this.flushScheduled)
        this.flushScheduled = null
      }
    } else if (!this.flushScheduled) {
      this.flushScheduled = setTimeout(() => {
        this.flushScheduled = null
        this.flushLogQueue()
      }, FLUSH_DEBOUNCE_MS)
    }
  }

  debug(message: string, context?: LogContext): void {
    if (config.env.isDevelopment || config.features.debug) {
      console.log(this.formatMessage('debug', message, context))
    }
    this.enqueueForShipping('debug', message, context)
  }

  info(message: string, context?: LogContext): void {
    console.info(this.formatMessage('info', message, context))
    this.enqueueForShipping('info', message, context)
  }

  warn(message: string, error?: Error | unknown, context?: LogContext): void {
    // If second arg is a plain object (not Error), treat it as context
    let err: Error | unknown
    let ctx: LogContext | undefined
    if (error !== undefined && error !== null && !(error instanceof Error) && typeof error === 'object') {
      ctx = { ...(error as LogContext), ...context }
      err = undefined
    } else {
      err = error
      ctx = context
    }

    const { logMessage, fullContext } = formatErrorLogParts(message, err, ctx)
    // CWE-209 / WS-JAVASCRIPT-01076: console gets the static message only.
    console.warn(this.formatMessage('warn', message))
    this.enqueueForShipping('warn', logMessage, fullContext)
  }

  /** Whitelist-safe warn/error dispatch — avoids obj[level]() (CWE-94). */
  logWarnOrError(level: 'warn' | 'error', message: string, error?: Error | unknown): void {
    if (level === 'warn') {
      this.warn(message, error)
    } else {
      this.error(message, error)
    }
  }

  error(message: string, error?: Error | unknown, context?: LogContext): void {
    // If second arg is a plain object (not Error), treat it as context so calls like
    // logger.error('Introspection timeout', { connectorId }) don't produce "[object Object]"
    let err: Error | unknown
    let ctx: LogContext | undefined
    if (error !== undefined && error !== null && !(error instanceof Error) && typeof error === 'object') {
      ctx = { ...(error as LogContext), ...context }
      err = undefined
    } else {
      err = error
      ctx = context
    }

    const { logMessage, fullContext } = formatErrorLogParts(message, err, ctx)
    // CWE-209 / WS-JAVASCRIPT-01076: console gets the static message only.
    console.error(this.formatMessage('error', message))
    this.enqueueForShipping('error', logMessage, fullContext)
  }

  // API-specific logging
  apiRequest(method: string, url: string, data?: unknown): void {
    this.debug(`API ${method} ${url}`, data ? { data } : undefined)
  }

  apiResponse(method: string, url: string, status: number, duration: number): void {
    const message = `API ${method} ${url} - ${status}`
    const context = { duration: `${duration}ms` }
    if (status >= 400) {
      this.warn(message, undefined, context)
    } else {
      this.debug(message, context)
    }
  }

  apiError(method: string, url: string, error: unknown): void {
    this.error(`API ${method} ${url} failed`, error)
  }

  // User action logging
  userAction(action: string, details?: LogContext): void {
    this.info(`User action: ${action}`, details)
  }

  // Performance logging
  performance(metric: string, value: number, unit: string = 'ms'): void {
    this.debug(`Performance: ${metric}`, { value, unit })
  }
}

// Export singleton instance
export const logger = new Logger()

// Export types for TypeScript support
export type { LogLevel, LogContext }
